import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, usd, formatApiErrorDetail, CARD_PAYMENTS_ENABLED, rememberEmail } from "../lib/api";
import { useRegistration } from "../context/RegistrationContext";
import { useSponsorForm } from "../hooks/useSponsorForm";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { TermsCheckbox } from "../components/TermsCheckbox";
import { SponsorHero, PackagePicker, QuantityPicker, AllocationList, SponsorDetails, VipGuests, SponsorPayment, SponsorSummary } from "../components/sponsor/SponsorParts";
import { ShieldCheck } from "lucide-react";

function validateSponsor(type, sponsor) {
  if (!sponsor.email && !sponsor.business_email) return "Please provide a contact email.";
  if (type === "individual" && (!sponsor.first_name || !sponsor.last_name)) return "Please provide your name.";
  if (type === "corporate" && !sponsor.company) return "Please provide the company/organization name.";
  return null;
}

function submitLabel({ closed, submitting, method, total }) {
  if (closed) return "Registration Closed";
  if (submitting) return "Processing...";
  if (method === "stripe") return `Sponsor & Pay ${usd(total)}`;
  return `Sponsor & Get Bank Details (${usd(total)})`;
}

export default function Sponsor() {
  const navigate = useNavigate();
  const { config } = useRegistration();
  const form = useSponsorForm(config?.sponsor_packages || []);
  const [type, setType] = useState("individual");
  const [sponsor, setSponsor] = useState({});
  const [anonymous, setAnonymous] = useState(false);
  const [ackName, setAckName] = useState("");
  const [method, setMethod] = useState(CARD_PAYMENTS_ENABLED ? "stripe" : "bank");
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const closed = config?.registration_closed;

  const submit = async () => {
    const err = validateSponsor(type, sponsor);
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        sponsor_type: type, sponsor, anonymous, acknowledgment_name: ackName,
        package: form.pkgKey, quantity: form.qty,
        allocations: form.allocations.map((a) => ({ mode: a.mode, nominee: a.mode === "nominate" ? a.nominee : null })),
        vip_guests: form.guests.map((g) => g.name.trim()).filter(Boolean),
        payment_method: method, origin_url: window.location.origin,
        terms_accepted_at: new Date().toISOString(),
      };
      const { data } = await api.post("/sponsorships", payload);
      rememberEmail(data.reference, sponsor.business_email || sponsor.email);
      if (method === "stripe") {
        if (data.checkout_url) { window.location.href = data.checkout_url; return; }
        toast.error("Unable to start Stripe checkout.");
      } else {
        toast.success("Sponsorship submitted!");
        navigate(`/sponsorship/${data.reference}`);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div>
      <Navbar />
      <SponsorHero />
      <PackagePicker packages={config?.sponsor_packages || []} pkgKey={form.pkgKey} onChoose={form.choosePkg} termsUrl={config?.legal?.terms_url} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6" data-testid="sponsor-form">
          <QuantityPicker pkg={form.pkg} qty={form.qty} children={form.children} onChange={form.setQtyChecked} />
          <AllocationList allocations={form.allocations} setAlloc={form.setAlloc} setNominee={form.setNominee} />
          <SponsorDetails type={type} setType={setType} setSponsorField={(patch) => setSponsor((s) => ({ ...s, ...patch }))}
            ackName={ackName} setAckName={setAckName} anonymous={anonymous} setAnonymous={setAnonymous} />
          <VipGuests guests={form.guests} seats={form.seats} setGuestName={form.setGuestName} />
          <SponsorPayment method={method} setMethod={setMethod} bank={config?.bank_details || {}} />
          <TermsCheckbox id="sponsor-accept-terms" checkboxTestId="sponsor-accept-terms-checkbox" linkPrefix="sponsor" checked={acceptedTerms} onChange={setAcceptedTerms} />
          <button data-testid="sponsor-submit" onClick={submit} disabled={submitting || closed || !acceptedTerms} className="w-full rounded-full bg-[#008057] hover:bg-[#054D34] disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 font-bold text-lg flex items-center justify-center gap-2">
            <ShieldCheck size={20} /> {submitLabel({ closed, submitting, method, total: form.total })}
          </button>
        </div>
        <div className="lg:col-span-1">
          <SponsorSummary pkg={form.pkg} qty={form.qty} children={form.children} seats={form.seats} total={form.total} />
        </div>
      </section>
      <Footer camp={config} />
    </div>
  );
}
